import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import WhatsAppBooking from '../components/common/WhatsAppBooking';
import { supabase } from '../lib/supabase';

interface ServiceCard {
  id: string;
  service_key: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  display_order: number;
  active: boolean;
}

const Services = () => {
  const { t } = useTranslation();
  const [serviceCards, setServiceCards] = useState<ServiceCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServiceCards();
  }, []);

  const fetchServiceCards = async () => {
    try {
      const { data, error } = await supabase
        .from('website_service_cards')
        .select('*')
        .eq('active', true)
        .order('display_order');

      if (error) throw error;
      setServiceCards(data || []);
    } catch (error) {
      console.error('Error fetching service cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const ToothIcon = () => (
    <svg 
      className="w-12 h-12" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M12 5.5c1.5-2 4-2.5 5.5-1.5 1.5 1 2 3.5 1 6.5 0 0 .5 6.5-6.5 9-7-2.5-6.5-9-6.5-9-1-3-.5-5.5 1-6.5 1.5-1 4-.5 5.5 1.5Z"/>
    </svg>
  );

  const services = serviceCards.map(card => ({
    id: card.service_key,
    icon: <ToothIcon />,
    title: card.title || t(`services.${card.service_key}.title`),
    description: card.description || t(`services.${card.service_key}.description`),
    details: t(`services.${card.service_key}.details`, { returnObjects: true }),
    image: card.image_url || ''
  }));

  return (
    <>
      {/* Services Hero */}
      <div className="bg-primary-600 pt-32 pb-20 px-4">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-6">{t('services.title')}</h1>
            <p className="text-xl text-white/80">{t('services.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Services List */}
      <section className="section bg-white">
        <div className="container">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
          <div className="space-y-20">
            {services.map((service, index) => (
              <div 
                id={service.id}
                key={service.id} 
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 0 ? '' : 'lg:flex-row-reverse'
                }`}
              >
                <div className={`${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="flex items-center mb-4">
                    <div className="text-primary-500">{service.icon}</div>
                    <h2 className="text-3xl font-bold ml-4">{service.title}</h2>
                  </div>
                  <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.details.map((detail, i) => (
                      <li key={i} className="flex items-start">
                        <span className="bg-primary-100 text-primary-600 p-1 rounded-full mr-3 mt-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </span>
                        <span className="text-gray-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href="#appointment"
                    className="btn bg-[#25D366] hover:bg-[#20BD5C] text-white inline-flex items-center gap-2"
                  >
                    <MessageCircle size={18} />
                    {t('nav.book')}
                  </a>
                </div>
                
                <div className={`${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="rounded-lg shadow-xl w-full h-full object-cover"
                    style={{ maxHeight: '400px' }}
                  />
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>
      
      <WhatsAppBooking />
    </>
  );
};

export default Services;